import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import {
  Add,
  AdminPanelSettings,
  AutoAwesome,
  Apps,
  CheckCircle,
  DeliveryDining,
  Handyman,
  Language as LanguageIcon,
  PhoneIphone,
  Refresh,
  Search,
  Settings,
  Storefront,
  Translate,
  WarningAmber,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import RowActions from '../../components/ui/RowActions';
import api from '../../services/api';
import {
  getTranslationCatalog,
  getTranslationRows,
  provisionLanguage,
  suggestTranslation,
  type TranslationCatalogLanguage,
  type TranslationCoverage,
  type TranslationRow,
  type TranslationRowStatus,
} from '../../services/platform/localeService';
import { useI18n } from '../../i18n/I18nProvider';
import { PLATFORM_PRIMARY } from '../../theme/brand';

const TEAL = PLATFORM_PRIMARY;
const SLATE = '#0f172a';

type FilterMode = 'all' | 'missing' | 'translated';
type AppId = 'all' | 'admin' | 'customer' | 'partner' | 'vendor' | 'provider';

interface AppModule {
  id: AppId;
  label: string;
  description: string;
  namespaces: string[];
  englishCount: number;
}

const FALLBACK_APPS: AppModule[] = [
  { id: 'all', label: 'All apps', description: 'Every unique key once — shared "common" keys are counted a single time', namespaces: [], englishCount: 0 },
  { id: 'admin', label: 'Admin Panel', description: 'Web admin — dashboard, settings, hubs, operations', namespaces: ['admin', 'common', 'food', 'grocery', 'transport', 'delivery', 'on_demand', 'events', 'video_consultation', 'booking'], englishCount: 0 },
  { id: 'customer', label: 'Customer App', description: 'Customer mobile — home, rides, food, grocery, profile', namespaces: ['mobile', 'transport_mobile', 'common'], englishCount: 0 },
  { id: 'partner', label: 'Partner App', description: 'Partner / driver mobile — jobs, earnings, profile', namespaces: ['partner', 'common'], englishCount: 0 },
  { id: 'vendor', label: 'Vendor App', description: 'Vendor / merchant mobile — orders, menu, earnings', namespaces: ['vendor', 'common'], englishCount: 0 },
  { id: 'provider', label: 'Provider App', description: 'On-demand service provider — jobs, quotes, availability, profile', namespaces: ['provider', 'common'], englishCount: 0 },
];

const APP_ICON: Record<AppId, typeof AdminPanelSettings> = {
  all: Apps,
  admin: AdminPanelSettings,
  customer: PhoneIphone,
  partner: DeliveryDining,
  vendor: Storefront,
  provider: Handyman,
};

const STATUS_META: Record<TranslationRowStatus, { label: string; color: 'success' | 'warning' | 'default' | 'info'; hint: string }> = {
  source: { label: 'Source', color: 'default', hint: 'English is the source language' },
  translated: { label: 'Translated', color: 'success', hint: 'Has a translated value' },
  reviewed: { label: 'Confirmed', color: 'info', hint: 'Saved by an admin — same as English on purpose (e.g. a brand name)' },
  keep_english: { label: 'Kept English', color: 'default', hint: 'Code or acronym (OTP, KYC, RTL…) that stays in English' },
  still_english: { label: 'Untranslated', color: 'warning', hint: 'Value is still the English text' },
  absent: { label: 'Missing', color: 'warning', hint: 'No value saved for this language' },
};

function formatPct(value: number | undefined) {
  const v = value ?? 0;
  return v >= 100 ? '100%' : `${v.toFixed(1)}%`;
}

type FormState = { namespace: string; key: string; value: string; english: string; existing: boolean };
const EMPTY_FORM: FormState = { namespace: 'admin', key: '', value: '', english: '', existing: false };

export default function TranslatorMasterPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { reloadTranslations, language: uiLanguage, t } = useI18n();
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [language, setLanguage] = useState('en');
  const [appId, setAppId] = useState<AppId>('all');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [suggestNote, setSuggestNote] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [provisioningCode, setProvisioningCode] = useState<string | null>(null);
  const [forceConfirm, setForceConfirm] = useState<TranslationCatalogLanguage | null>(null);
  /** Keep polling catalog after Auto-translate starts (background job). */
  const [watchCatalogUntil, setWatchCatalogUntil] = useState(0);

  const { data: catalog, isLoading: catalogLoading, isFetching: catalogFetching, refetch: refetchCatalog } = useQuery({
    queryKey: ['translation-catalog'],
    queryFn: getTranslationCatalog,
    refetchInterval: (query) => {
      const langs = query.state.data?.languages || [];
      if (langs.some((l) => l.provisioning)) return 4000;
      if (Date.now() < watchCatalogUntil) return 5000;
      return false;
    },
  });

  const languages = catalog?.languages || [];
  const apps = (catalog?.apps?.length ? catalog.apps : FALLBACK_APPS) as AppModule[];
  const selectedApp = apps.find((a) => a.id === appId) || apps[0] || FALLBACK_APPS[0];
  const appLabels = apps.filter((a) => a.id !== 'all').map((a) => a.label);
  const languageSelectValue = languages.some((l) => l.code === language)
    ? language
    : (languages[0]?.code || '');

  useEffect(() => {
    if (!languages.length) return;
    if (!languages.some((l) => l.code === language)) {
      const def = languages.find((l) => l.isDefault)?.code || languages[0].code;
      setLanguage(def);
    }
  }, [languages, language]);

  const selectedLang = languages.find((l) => l.code === language);
  const isSource = language === 'en';
  const langBusy = !!selectedLang?.provisioning || provisioningCode === language;

  const { data: rowData, isLoading: rowsLoading, isFetching: rowsFetching } = useQuery({
    queryKey: ['translation-rows', language, selectedApp.id],
    queryFn: () => getTranslationRows(language, selectedApp.id),
    enabled: !!language,
    refetchInterval: langBusy ? 6000 : false,
  });
  const allRows: TranslationRow[] = rowData?.rows || [];

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((row) => {
      if (filter === 'missing' && !row.missing) return false;
      if (filter === 'translated' && row.missing) return false;
      if (!q) return true;
      return (
        row.key.toLowerCase().includes(q) ||
        row.namespace.toLowerCase().includes(q) ||
        row.value.toLowerCase().includes(q) ||
        row.english.toLowerCase().includes(q)
      );
    });
  }, [allRows, search, filter]);

  const missingInApp = allRows.filter((r) => r.missing).length;

  const refreshAll = async () => {
    await Promise.all([
      refetchCatalog(),
      queryClient.invalidateQueries({ queryKey: ['translation-rows'] }),
    ]);
  };

  const showMissing = (code: string, app: AppId = 'all') => {
    setLanguage(code);
    setAppId(app);
    setFilter('missing');
    setSearch('');
    window.setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const openEditor = (row: TranslationRow) => {
    setForm({
      namespace: row.namespace,
      key: row.key,
      value: row.missing ? '' : row.value,
      english: row.english,
      existing: true,
    });
    setFormError(null);
    setSuggestNote(null);
    setDialogOpen(true);
  };

  const nextMissingAfter = (id: string) => {
    const missing = rows.filter((r) => r.missing);
    const idx = missing.findIndex((r) => r.id === id);
    return missing.find((r, i) => i > idx && r.id !== id) || missing.find((r) => r.id !== id) || null;
  };

  const saveMutation = useMutation({
    mutationFn: async ({ openNext }: { openNext: boolean }) => {
      await api.put('/admin/translations', {
        language,
        namespace: form.namespace,
        key: form.key.trim(),
        value: form.value,
      });
      return { openNext, next: openNext ? nextMissingAfter(`${form.namespace}::${form.key.trim()}`) : null };
    },
    onSuccess: async ({ openNext, next }) => {
      queryClient.invalidateQueries({ queryKey: ['translation-rows'] });
      queryClient.invalidateQueries({ queryKey: ['translation-catalog'] });
      if (language === uiLanguage) await reloadTranslations();
      if (openNext && next) {
        openEditor(next);
        return;
      }
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      if (openNext) setBanner({ text: `No more untranslated keys in ${selectedApp.label} for ${language.toUpperCase()}.`, severity: 'success' });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setFormError(msg || 'Could not save the translation. Please try again.');
    },
  });

  const suggestMutation = useMutation({
    mutationFn: () => suggestTranslation(language, form.english || form.value),
    onSuccess: (res) => {
      setForm((s) => ({ ...s, value: res.value }));
      setSuggestNote(
        res.sameAsEnglish
          ? 'The translation service returned the English text unchanged. Type the translation yourself, or save as-is to confirm it stays in English (e.g. a brand name).'
          : 'Machine suggestion filled in — review it, then save.',
      );
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSuggestNote(msg || 'Translation service unavailable. Type the translation manually.');
    },
  });

  const provisionMutation = useMutation({
    mutationFn: ({ code, force }: { code: string; force: boolean }) => provisionLanguage(code, force, false),
    onMutate: ({ code }) => setProvisioningCode(code),
    onSettled: () => setProvisioningCode(null),
    onSuccess: (result, vars) => {
      setWatchCatalogUntil(Date.now() + 3 * 60 * 1000);
      queryClient.invalidateQueries({ queryKey: ['translation-catalog'] });
      queryClient.invalidateQueries({ queryKey: ['locale-languages'] });
      setBanner({
        severity: 'success',
        text: result?.started
          ? vars.force
            ? `Re-translating every ${vars.code.toUpperCase()} key across all apps. Counts update as keys are saved.`
            : `Translating the untranslated ${vars.code.toUpperCase()} keys across all apps. Counts update as keys are saved.`
          : `Auto-translate completed for ${vars.code.toUpperCase()}.`,
      });
      window.setTimeout(refreshAll, 6000);
      window.setTimeout(refreshAll, 45000);
    },
    onError: (_e, vars) => {
      setBanner({ severity: 'error', text: `Auto-translate failed for ${vars.code.toUpperCase()}. Check the backend connection.` });
    },
  });

  const columns: GridColDef<TranslationRow>[] = [
    {
      field: 'key',
      headerName: 'Key',
      flex: 1.2,
      minWidth: 200,
      renderCell: (params) => (
        <Box minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.78rem' }}>
            {params.row.key}
          </Typography>
          <Typography variant="caption" color="text.secondary">{params.row.namespace}</Typography>
        </Box>
      ),
    },
    {
      field: 'english',
      headerName: 'English (source)',
      flex: 1.3,
      minWidth: 200,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap title={String(params.value || '')}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'value',
      headerName: isSource ? 'Value' : `${language.toUpperCase()} translation`,
      flex: 1.4,
      minWidth: 220,
      renderCell: (params) => (
        <Typography
          variant="body2"
          noWrap
          title={String(params.value || '')}
          color={params.row.missing ? 'text.disabled' : 'text.primary'}
        >
          {params.row.status === 'absent' ? '—' : params.value}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const meta = STATUS_META[params.row.status];
        return (
          <Tooltip title={meta.hint}>
            <Chip size="small" label={meta.label} color={meta.color} variant={params.row.missing ? 'filled' : 'outlined'} sx={{ height: 22 }} />
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 120,
      sortable: false,
      renderCell: (params) =>
        params.row.missing && !isSource ? (
          <Button size="small" startIcon={<Translate fontSize="small" />} onClick={() => openEditor(params.row)} sx={{ textTransform: 'none', fontWeight: 700, color: TEAL }}>
            Translate
          </Button>
        ) : (
          <RowActions onEdit={() => openEditor(params.row)} />
        ),
    },
  ];

  const renderAppCoverage = (app: AppModule, cov: TranslationCoverage | undefined, selected: boolean) => {
    if (isSource || !cov) {
      return (
        <>
          <Typography variant="h5" fontWeight={800} sx={{ mt: 1, letterSpacing: '-0.03em', color: selected ? TEAL : SLATE }}>
            {catalogLoading ? '…' : app.englishCount}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            English source keys
          </Typography>
        </>
      );
    }
    const done = cov.total - cov.missing;
    return (
      <>
        <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 1 }}>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.03em', color: selected ? TEAL : SLATE }}>
            {formatPct(cov.coverage)}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {done}/{cov.total}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={Math.min(100, cov.coverage)}
          sx={{ mt: 0.75, height: 5, borderRadius: 99, bgcolor: alpha(TEAL, 0.12), '& .MuiLinearProgress-bar': { bgcolor: cov.missing ? '#f59e0b' : TEAL } }}
        />
        {cov.missing > 0 ? (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${cov.missing} untranslated`}
            onClick={(e) => {
              e.stopPropagation();
              showMissing(language, app.id);
            }}
            sx={{ mt: 0.75, height: 22, cursor: 'pointer' }}
          />
        ) : (
          <Typography variant="caption" sx={{ mt: 0.75, display: 'block', color: TEAL, fontWeight: 700 }}>
            Complete
          </Typography>
        )}
      </>
    );
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Localization"
        title="Translator Master"
        subtitle={`Strings for the ${appLabels.slice(0, -1).join(', ')} and ${appLabels[appLabels.length - 1] || 'Provider App'}. English is the source language — add a language once and its English keys are translated automatically.`}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => navigate('/config/masters?group=locale&type=languages')}
              sx={{ borderColor: alpha(TEAL, 0.35), color: TEAL, '&:hover': { borderColor: TEAL, bgcolor: alpha(TEAL, 0.06) } }}
            >
              {t('admin.translator.manage_languages', 'Manage languages')}
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                const ns = selectedApp.id === 'all' ? 'admin' : selectedApp.namespaces[0] || 'admin';
                setForm({ ...EMPTY_FORM, namespace: ns });
                setFormError(null);
                setSuggestNote(null);
                setDialogOpen(true);
              }}
              sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }}
            >
              {t('admin.translator.add_string', 'Add string')}
            </Button>
          </Stack>
        }
      />

      <Alert
        severity="info"
        icon={<LanguageIcon />}
        sx={{
          mb: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${alpha(TEAL, 0.25)}`,
          bgcolor: alpha(TEAL, 0.06),
          color: SLATE,
          '& .MuiAlert-icon': { color: TEAL },
        }}
      >
        {t(
          'admin.translator.flow_hint_v2',
          'Flow: Master Data → Languages (add/activate) → Auto-translate from English → fix anything left in the untranslated list → admin panel and mobile apps load active languages.',
        )}
      </Alert>

      {banner && (
        <Alert severity={banner.severity} sx={{ mb: 2, borderRadius: 2 }} onClose={() => setBanner(null)}>
          {banner.text}
        </Alert>
      )}

      {/* Active languages */}
      <Paper elevation={0} sx={{ mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: alpha(TEAL, 0.05),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={800} sx={{ color: SLATE }}>
              {t('admin.translator.active_languages', 'Active languages')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Shown in the admin language switcher and the Customer, Partner, Vendor and Provider app language screens.
              Counts cover {catalog?.englishKeys ?? '…'} unique English source keys across all apps. A language is ready only when every key is translated.
            </Typography>
          </Box>
          <Tooltip title="Refresh counts, percentages and the table">
            <span>
              <IconButton size="small" onClick={refreshAll} disabled={catalogFetching}>
                {catalogFetching ? <CircularProgress size={16} sx={{ color: TEAL }} /> : <Refresh fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box sx={{ p: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' } }}>
          {catalogLoading && !languages.length && (
            <Box sx={{ py: 4, gridColumn: '1 / -1', textAlign: 'center' }}>
              <CircularProgress size={28} sx={{ color: TEAL }} />
            </Box>
          )}
          {!catalogLoading && !languages.length && (
            <Alert severity="warning" sx={{ gridColumn: '1 / -1' }}>
              No active languages yet. Add languages under Master Data → Languages.
            </Alert>
          )}
          {languages.map((lang) => {
            const selected = language === lang.code;
            const busy = provisioningCode === lang.code || !!lang.provisioning;
            const source = lang.code === 'en';
            const missing = lang.missingCount || 0;
            const total = catalog?.englishKeys || lang.keyCount;
            const complete = source || missing === 0;
            const breakdown = [
              lang.stillEnglishCount ? `${lang.stillEnglishCount} still in English` : '',
              lang.absentCount ? `${lang.absentCount} with no value` : '',
            ].filter(Boolean).join(', ');
            const statusTip = busy
              ? 'Auto-translate is running — counts refresh automatically.'
              : complete
                ? 'Ready — every source key has a translation.'
                : `Incomplete — ${missing} of ${total} keys are not translated yet${breakdown ? ` (${breakdown})` : ''}. Click "${missing} untranslated" to translate them.`;
            return (
              <Paper
                key={lang.code}
                elevation={0}
                onClick={() => setLanguage(lang.code)}
                sx={{
                  p: 2,
                  borderRadius: 2.5,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: selected ? TEAL : 'divider',
                  bgcolor: selected ? alpha(TEAL, 0.04) : '#fff',
                  '&:hover': { borderColor: alpha(TEAL, 0.55) },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ color: SLATE }}>
                        {lang.nativeName || lang.name}
                      </Typography>
                      <Chip size="small" label={lang.code.toUpperCase()} sx={{ height: 22, fontWeight: 700 }} />
                      {source && <Chip size="small" label="Source language" color="info" variant="outlined" sx={{ height: 22 }} />}
                      {lang.isDefault && <Chip size="small" label={t('admin.translator.default', 'Default')} color="primary" variant="outlined" sx={{ height: 22 }} />}
                      {lang.rtl && <Chip size="small" label={t('admin.translator.rtl', 'RTL')} sx={{ height: 22 }} />}
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {source
                        ? `${lang.name} · ${total} source keys — every other language is translated from these`
                        : `${lang.name} · ${lang.translatedCount ?? 0}/${total} translated · ${formatPct(lang.coverage)}`}
                    </Typography>
                  </Box>
                  <Tooltip title={statusTip}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                      {busy ? (
                        <CircularProgress size={18} sx={{ color: TEAL }} />
                      ) : complete ? (
                        <>
                          <CheckCircle sx={{ color: TEAL, fontSize: 20 }} />
                          {!source && <Typography variant="caption" fontWeight={700} sx={{ color: TEAL }}>Ready</Typography>}
                        </>
                      ) : (
                        <>
                          <WarningAmber color="warning" fontSize="small" />
                          <Typography variant="caption" fontWeight={700} color="warning.main">Incomplete</Typography>
                        </>
                      )}
                    </Box>
                  </Tooltip>
                </Stack>
                <LinearProgress
                  variant={busy ? 'indeterminate' : 'determinate'}
                  value={busy ? undefined : Math.min(100, lang.coverage || 0)}
                  sx={{
                    mt: 1.5,
                    height: 6,
                    borderRadius: 99,
                    bgcolor: alpha(TEAL, 0.12),
                    '& .MuiLinearProgress-bar': { bgcolor: complete ? TEAL : '#f59e0b', borderRadius: 99 },
                  }}
                />
                {!source && (
                  <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap alignItems="center">
                    <Tooltip title={missing ? `Translate the ${missing} untranslated keys in every app` : 'Nothing left to translate'}>
                      <span>
                        <Button
                          size="small"
                          variant={selected ? 'contained' : 'outlined'}
                          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Translate />}
                          disabled={busy || missing === 0}
                          onClick={(e) => {
                            e.stopPropagation();
                            provisionMutation.mutate({ code: lang.code, force: false });
                          }}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 700,
                            ...(selected
                              ? { bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }
                              : { borderColor: alpha(TEAL, 0.4), color: TEAL }),
                          }}
                        >
                          {busy ? t('admin.translator.provisioning', 'Translating…') : 'Translate missing'}
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title="Machine-translate every key again across all apps (admin-saved values are kept)">
                      <span>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<AutoAwesome fontSize="small" />}
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            setForceConfirm(lang);
                          }}
                          sx={{ textTransform: 'none', fontWeight: 700, color: TEAL }}
                        >
                          Re-translate all
                        </Button>
                      </span>
                    </Tooltip>
                    {missing > 0 && (
                      <Tooltip title="Show these keys in the table so you can translate them">
                        <Chip
                          size="small"
                          color="warning"
                          label={`${missing} untranslated`}
                          onClick={(e) => {
                            e.stopPropagation();
                            showMissing(lang.code);
                          }}
                          sx={{ cursor: 'pointer', fontWeight: 700 }}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                )}
                {lang.provisionError && !busy && missing > 0 && (
                  <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1, lineHeight: 1.35 }}>
                    {lang.provisionError}
                  </Typography>
                )}
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Apps — coverage for the selected language */}
      <Typography variant="subtitle2" fontWeight={800} sx={{ color: SLATE, mb: 0.5 }}>
        {isSource ? 'English source keys by app' : `${selectedLang?.nativeName || language.toUpperCase()} coverage by app`}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
        "All apps" counts each key once ({catalog?.englishKeys ?? '…'} keys). The {catalog?.sharedKeys ?? '…'} shared "common" keys appear in every app,
        so the per-app numbers add up to more than the total.
      </Typography>
      <Box
        sx={{
          mb: 2.5,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(6, 1fr)' },
        }}
      >
        {apps.map((app) => {
          const selected = app.id === selectedApp.id;
          const Icon = APP_ICON[app.id] || AdminPanelSettings;
          const cov = selectedLang?.apps?.[app.id];
          return (
            <Paper
              key={app.id}
              elevation={0}
              onClick={() => setAppId(app.id)}
              sx={{
                p: 2,
                borderRadius: 2.5,
                cursor: 'pointer',
                border: '2px solid',
                borderColor: selected ? TEAL : 'divider',
                bgcolor: selected ? alpha(TEAL, 0.05) : '#fff',
                transition: 'border-color .15s, box-shadow .15s',
                '&:hover': { borderColor: alpha(TEAL, 0.55), boxShadow: `0 8px 24px ${alpha(SLATE, 0.06)}` },
              }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: selected ? TEAL : alpha(SLATE, 0.06),
                    color: selected ? '#fff' : SLATE,
                    flexShrink: 0,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Box minWidth={0} flex={1}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ color: SLATE }} noWrap>
                    {app.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {app.description}
                  </Typography>
                  {renderAppCoverage(app, cov, selected)}
                  {!isSource && cov && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {app.englishCount} English keys
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Box>

      {/* Catalog for selected app */}
      <Box ref={tableRef} sx={{ scrollMarginTop: 80 }}>
        <DataTableCard
          title={`${selectedApp.label} — ${t('admin.translator.catalog', 'Translation catalog')}`}
          subtitle={`${allRows.length} keys · ${language.toUpperCase()}${!isSource ? ` · ${missingInApp} untranslated` : ' · source language'}`}
          minHeight={520}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
            <TextField
              select
              size="small"
              label="Language"
              value={languageSelectValue}
              disabled={!languages.length}
              onChange={(e) => setLanguage(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              {!languages.length && (
                <MenuItem value="">
                  {catalogLoading ? t('common.label.loading', 'Loading…') : t('admin.translator.no_languages', 'No languages')}
                </MenuItem>
              )}
              {languages.map((l) => (
                <MenuItem key={l.code} value={l.code}>
                  {l.nativeName || l.name} ({l.code.toUpperCase()})
                  {l.code === 'en' ? ' · source' : (l.missingCount || 0) === 0 ? ' ✓' : ` · ${l.missingCount} left`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="App"
              value={selectedApp.id}
              onChange={(e) => setAppId(e.target.value as AppId)}
              sx={{ minWidth: 170 }}
            >
              {apps.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.label}</MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              placeholder={t('admin.translator.search_placeholder', 'Search key or value…')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <ToggleButtonGroup
              exclusive
              size="small"
              value={filter}
              onChange={(_e, v) => v && setFilter(v)}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.5 },
                '& .Mui-selected': { bgcolor: `${alpha(TEAL, 0.12)} !important`, color: `${TEAL} !important` },
              }}
            >
              <ToggleButton value="all">{t('admin.translator.filter_all', 'All')}</ToggleButton>
              <ToggleButton value="missing" disabled={isSource}>
                {`Untranslated${!isSource ? ` (${missingInApp})` : ''}`}
              </ToggleButton>
              <ToggleButton value="translated">{t('admin.translator.filter_translated', 'Translated')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {filter === 'missing' && !isSource && missingInApp > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 1.5, borderRadius: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    const first = rows.find((r) => r.missing);
                    if (first) openEditor(first);
                  }}
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                >
                  Start translating
                </Button>
              }
            >
              {missingInApp} key(s) in {selectedApp.label} are not translated to {selectedLang?.name || language.toUpperCase()}.
              Translate them one by one ("Save &amp; next" moves to the next key) or use "Translate missing" on the language card.
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Viewing <strong>{selectedApp.label}</strong>
            {selectedLang ? ` · ${selectedLang.nativeName || selectedLang.name}` : ''} · {selectedApp.englishCount} English source keys
            {rowsFetching && !rowsLoading ? ' · refreshing…' : ''}
          </Typography>

          <EnhancedDataGrid
            rows={rows}
            columns={columns}
            loading={rowsLoading}
            getRowId={(r) => r.id as string}
            autoHeight
            exportFileName={`translator-${selectedApp.id}-${language}`}
          />
        </DataTableCard>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {form.existing ? `Translate to ${selectedLang?.name || language.toUpperCase()}` : t('admin.translator.add_string', 'Add string')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
            {form.existing ? (
              <>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Key</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                    {form.namespace} · {form.key}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="English (source)" value={form.english} InputProps={{ readOnly: true }} multiline minRows={1} />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label="App area"
                    value={form.namespace}
                    onChange={(e) => setForm((s) => ({ ...s, namespace: e.target.value }))}
                  >
                    {(selectedApp.id === 'all' ? Array.from(new Set(apps.flatMap((a) => a.namespaces))) : selectedApp.namespaces).map((ns) => (
                      <MenuItem key={ns} value={ns}>{ns}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('admin.translator.key', 'Key')}
                    value={form.key}
                    onChange={(e) => setForm((s) => ({ ...s, key: e.target.value }))}
                    placeholder="e.g. nav.dashboard"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                autoFocus={form.existing}
                label={`${t('admin.translator.value', 'Value')} (${language.toUpperCase()})`}
                value={form.value}
                multiline
                minRows={2}
                inputProps={{ dir: selectedLang?.rtl ? 'rtl' : 'ltr' }}
                onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
                helperText={
                  form.existing && !isSource && form.value.trim() && form.value === form.english
                    ? 'Same as English — saving confirms this key intentionally stays in English.'
                    : ' '
                }
              />
            </Grid>
            {form.existing && !isSource && (
              <Grid item xs={12}>
                <Button
                  size="small"
                  startIcon={suggestMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome fontSize="small" />}
                  disabled={suggestMutation.isPending || !form.english}
                  onClick={() => suggestMutation.mutate()}
                  sx={{ textTransform: 'none', fontWeight: 700, color: TEAL }}
                >
                  Suggest translation
                </Button>
                {suggestNote && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {suggestNote}
                  </Typography>
                )}
              </Grid>
            )}
            {formError && (
              <Grid item xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>{t('common.action.cancel', 'Cancel')}</Button>
          {form.existing && !isSource && filter === 'missing' && (
            <Button
              variant="outlined"
              disabled={!form.key || !form.value.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate({ openNext: true })}
              sx={{ borderColor: alpha(TEAL, 0.4), color: TEAL }}
            >
              Save &amp; next
            </Button>
          )}
          <Button
            variant="contained"
            disabled={!form.key.trim() || !form.value.trim() || saveMutation.isPending}
            onClick={() => saveMutation.mutate({ openNext: false })}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }}
          >
            {t('admin.translator.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!forceConfirm} onClose={() => setForceConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Re-translate all {forceConfirm?.name} keys?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every one of the {catalog?.englishKeys ?? ''} source keys in all apps (Admin Panel, Customer, Partner, Vendor and Provider)
            will be machine-translated again from English, replacing current machine translations. Values an admin saved by hand are kept.
            This runs in the background and can take a few minutes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setForceConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (forceConfirm) provisionMutation.mutate({ code: forceConfirm.code, force: true });
              setForceConfirm(null);
            }}
            sx={{ bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } }}
          >
            Re-translate all
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
